import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { mileageAPI, vehicleAPI } from '../api/client';
import { Gauge, TrendingUp, Fuel, Plus, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const MileageFuel = () => {
  const [logs, setLogs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ avgMPG: 0, totalMiles: 0, totalFuel: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, vehiclesRes] = await Promise.all([
        mileageAPI.list(),
        vehicleAPI.list()
      ]);
      setLogs(logsRes.data);
      setVehicles(vehiclesRes.data);
      calculateStats(logsRes.data);
    } catch (error) {
      toast.error('Failed to load mileage logs');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (logList) => {
    if (logList.length === 0) return;

    const totalFuel = logList.reduce((sum, log) => sum + (log.fuel_amount || 0), 0);
    const logsWithMPG = logList.filter(log => log.mpg);
    const avgMPG = logsWithMPG.length > 0
      ? logsWithMPG.reduce((sum, log) => sum + log.mpg, 0) / logsWithMPG.length
      : 0;

    const sortedLogs = [...logList].sort((a, b) => a.odometer - b.odometer);
    const totalMiles = sortedLogs.length > 1
      ? sortedLogs[sortedLogs.length - 1].odometer - sortedLogs[0].odometer
      : 0;

    setStats({ avgMPG, totalMiles, totalFuel });
  };

  const chartData = logs
    .filter(log => log.mpg)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(log => ({
      date: format(parseISO(log.date), 'dd MMM'),
      mpg: log.mpg,
      odometer: log.odometer
    }));

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 pb-6"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Mileage & Fuel</h1>
          <p className="text-muted-foreground mt-1">Track fuel efficiency and consumption</p>
        </div>
        <Button asChild size="lg" className="rounded-xl" data-testid="add-mileage-button">
          <Link to="/app/mileage/add">
            <Plus className="h-5 w-5 mr-2" />
            Add Log
          </Link>
        </Button>
      </div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 md:grid-cols-3"
      >
        <Card className="card-elevated glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">Average MPG</CardTitle>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-teal))]/10 text-[hsl(var(--brand-teal))] flex items-center justify-center">
                <Gauge className="h-5 w-5" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold counter-number" data-testid="avg-mpg">
              {stats.avgMPG.toFixed(1)} MPG
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Miles</CardTitle>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-sky))]/10 text-[hsl(var(--brand-sky))] flex items-center justify-center">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold counter-number" data-testid="total-miles">
              {stats.totalMiles.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Fuel</CardTitle>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-coral))]/10 text-[hsl(var(--brand-coral))] flex items-center justify-center">
                <Fuel className="h-5 w-5" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold counter-number" data-testid="total-fuel">
              {stats.totalFuel.toFixed(1)}L
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* MPG Trend Chart */}
      {chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Fuel Efficiency Trend</CardTitle>
              <CardDescription>MPG over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" label={{ value: 'MPG', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="mpg"
                    stroke="hsl(var(--brand-teal))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--brand-teal))', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Recent Logs */}
      {logs.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-2xl font-semibold mb-4">Recent Logs</h2>
          <div className="space-y-3" data-testid="mileage-logs-list">
            {logs.slice(0, 10).map((log, index) => (
              <motion.div
                key={log.log_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.05 }}
              >
                <Card className="card-hover">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-teal))]/10 flex items-center justify-center">
                          <Gauge className="h-6 w-6 text-[hsl(var(--brand-teal))]" />
                        </div>
                        <div>
                          <p className="font-medium font-mono">{log.odometer.toLocaleString()} miles</p>
                          <p className="text-sm text-muted-foreground">
                            {format(parseISO(log.date), 'dd MMM yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {log.mpg && (
                          <p className="text-xl font-semibold counter-number">{log.mpg.toFixed(1)} MPG</p>
                        )}
                        {log.fuel_amount && (
                          <p className="text-sm text-muted-foreground">{log.fuel_amount}L fuel</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ) : (
        <Card className="card-elevated">
          <CardContent className="py-16 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <Gauge className="h-10 w-10 text-primary" />
            </div>
            <h3 className="font-semibold text-2xl mb-2">No mileage logs yet</h3>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Start tracking your mileage and fuel consumption to monitor efficiency and costs.
            </p>
            <Button asChild size="lg" className="rounded-xl">
              <Link to="/app/mileage/add">
                <Plus className="h-5 w-5 mr-2" />
                Add Your First Log
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
};

export default MileageFuel;
